import { Box, Button } from '@material-ui/core';
import CloudDownloadOutlinedIcon from '@material-ui/icons/CloudDownloadOutlined';
import axios from 'axios';
import * as pdfjsLib from 'pdfjs-dist/build/pdf';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../hooks/auth';
import {
  apiHnsn,
  apiLaureano,
  apiLaureanoSoul,
  apiPhato,
} from '../../services/api';
import { fetchPhatoExamePdf } from '../../services/phatoExame';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

function LaudoView() {
  const { cdLaudo } = useParams() as any;
  const from = sessionStorage.getItem('@cedapp:from');
  const { tokenHnsn, tokenPhato, tokenLaureano, tokenLaureanoSoul } = useAuth();
  const viewerRef = useRef<HTMLDivElement>(null);
  const [phatoPdfUrl, setPhatoPdfUrl] = useState<string | null>(null);
  const [phatoFilename, setPhatoFilename] = useState('laudo.pdf');

  const getTokenLaudo = useCallback((): string => {
    if (from === 'LAUREANO_MV2000') {
      return `${apiLaureano.defaults.baseURL}/laudo/android/${tokenLaureano}/${cdLaudo}`;
    }
    if (from === 'LAUREANO') {
      return `${apiLaureanoSoul.defaults.baseURL}/laudo/android/${tokenLaureanoSoul}/${cdLaudo}`;
    }
    return `${apiHnsn.defaults.baseURL}/laudo/android/${tokenHnsn}/${cdLaudo}`;
  }, [cdLaudo, from, tokenHnsn, tokenLaureano, tokenLaureanoSoul]);

  const getPhatoPdfBytes = useCallback(async () => {
    return fetchPhatoExamePdf(
      apiPhato,
      cdLaudo,
      tokenPhato || sessionStorage.getItem('@cedapp:tokenPhato'),
    );
  }, [cdLaudo, tokenPhato]);

  const getPdfBlob = useCallback(async () => {
    if (from === 'PHATO') {
      const { bytes, filename } = await getPhatoPdfBytes();
      setPhatoFilename(filename);
      return {
        blob: new Blob([bytes], { type: 'application/pdf' }),
        filename,
        bytes,
      };
    }

    const response = await axios.get(getTokenLaudo(), {
      responseType: 'arraybuffer',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/pdf',
      },
    });

    return {
      blob: new Blob([response.data], { type: 'application/pdf' }),
      filename: 'laudo.pdf',
      bytes: new Uint8Array(response.data),
    };
  }, [from, getPhatoPdfBytes, getTokenLaudo]);

  const isiOS = (): boolean => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent);
  };

  const download = (): void => {
    getPdfBlob()
      .then(async ({ blob, filename }) => {
        await (window.navigator as any).share({
          files: [new File([blob], filename || phatoFilename, { type: 'application/pdf' })],
        });
      })
      .catch(() => {
        toast.error('Não foi possível baixar o laudo.');
      });
  };

  const isAndroid = (): boolean => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return /android/.test(userAgent);
  };

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    async function loadPdf() {
      try {
        let pdfSource: string | { data: Uint8Array } = getTokenLaudo();

        if (from === 'PHATO') {
          const { bytes, filename } = await getPhatoPdfBytes();
          objectUrl = URL.createObjectURL(
            new Blob([bytes], { type: 'application/pdf' }),
          );
          pdfSource = { data: bytes };

          if (!cancelled) {
            setPhatoFilename(filename);
            setPhatoPdfUrl(objectUrl);
          }
        } else if (!cancelled) {
          setPhatoPdfUrl(null);
        }

        if (!isAndroid() || cancelled) {
          return;
        }

        const timer = window.setTimeout(() => {
          const loadingTask = pdfjsLib.getDocument(pdfSource as any);

          loadingTask.promise.then((pdf: any) => {
            const numPages = pdf.numPages;
            const viewer = viewerRef.current;

            if (viewer) {
              viewer.innerHTML = '';

              for (let pageNum = 1; pageNum <= numPages; pageNum++) {
                pdf.getPage(pageNum).then((page: any) => {
                  const scale = 1.5;
                  const viewport = page.getViewport({ scale });
                  const canvas = document.createElement('canvas');
                  const context = canvas.getContext('2d');
                  canvas.height = viewport.height;
                  canvas.width = viewport.width;
                  viewer.appendChild(canvas);

                  const renderContext = {
                    canvasContext: context,
                    viewport,
                  };
                  page.render(renderContext);
                });
              }
            }
          });
        }, 100);

        return () => window.clearTimeout(timer);
      } catch (error) {
        if (!cancelled && from === 'PHATO') {
          toast.error('Não foi possível abrir o laudo do Phato.');
        }
        console.log(error);
      }
    }

    loadPdf();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [cdLaudo, from, getPhatoPdfBytes, getTokenLaudo]);

  return (
    <Box
      style={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isiOS() && (
        <Button
          onClick={download}
          style={{ margin: 8, alignSelf: 'flex-end' }}
          startIcon={<CloudDownloadOutlinedIcon />}
        >
          Baixar
        </Button>
      )}
      {!isAndroid() ? (
        <iframe
          title="laudo"
          src={from === 'PHATO' ? phatoPdfUrl || '' : getTokenLaudo()}
          style={{ width: '100%', height: '100%', border: 'none' }}
        />
      ) : (
        <div ref={viewerRef} style={{ width: '100%', height: '100%' }} />
      )}
    </Box>
  );
}

export default LaudoView;
